// Schedule → Agency Portal: the agency's portal, opened from inside CAERA.
//
// Shows a coordinator exactly what a contracting agency sees at its own link —
// the staffing board, the care plans, its nurses' credentialing — through the
// coordinator's own sign-in, never the agency's link. So the view is logged
// under the person's name ("Bisi Olowu viewed Extended Living's board"), it
// does not count as the agency opening its board, and care plans opened here
// are stamped with the person's name. Read-only: submitting a nurse or creating
// a signing link is the agency's to do, through its link.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { agencyMayUsePortal } from '../../lib/agencySlice';
import { AgencyPortal } from '../AgencyBoard';

export default function AgencyPortalView() {
  const [agencies, setAgencies] = useState(null);
  const [agencyId, setAgencyId] = useState(null);
  const [err, setErr] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    supabase.from('contracting_agencies').select('id, name, status').order('name').then(({ data, error }) => {
      if (error) { setErr(error.message); setAgencies([]); return; }
      const usable = (data || []).filter(a => agencyMayUsePortal(a.status));
      setAgencies(usable);
      if (usable.length) setAgencyId(usable[0].id);
    });
  }, []);

  // One object per agency, so the portal loads once rather than on every redraw.
  const access = useMemo(() => (agencyId ? { agencyId } : null), [agencyId]);

  // The board names a child by reference only; find the profile to share a
  // care plan from (its Schedule tab holds "Care plan for agencies").
  const openClient = useCallback(async (ref) => {
    const { data, error } = await supabase.from('clients').select('id').eq('client_id', ref).maybeSingle();
    if (error || !data) { setErr(error?.message || `Client ${ref} not found.`); return; }
    navigate(`/clients/${data.id}?tab=schedule`);
  }, [navigate]);

  return (
    <div>
      {agencies && agencies.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text3)' }}>Agency</span>
          <select className="form-select" style={{ maxWidth: 320 }} value={agencyId || ''} onChange={e => setAgencyId(e.target.value)}>
            {agencies.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      )}
      {err && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
      {agencies && agencies.length === 0 && !err && (
        <p style={{ color: 'var(--text3)', fontSize: 13 }}>No contracting agency is active.</p>
      )}
      {/* Keyed so switching agency starts the portal afresh. */}
      {access && <AgencyPortal key={agencyId} access={access} embedded onOpenClient={openClient} />}
    </div>
  );
}
